import React from "react";

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  siblingCount?: number;
}

function getPageRange(
  page: number,
  totalPages: number,
  siblingCount: number,
): (number | "...")[] {
  // Standard ellipsis algorithm: always show first + last, plus a window
  // of `siblingCount` on each side of the current page.
  const totalNumbers = siblingCount * 2 + 5; // first + last + current + 2 ellipses
  if (totalPages <= totalNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const left = Math.max(page - siblingCount, 2);
  const right = Math.min(page + siblingCount, totalPages - 1);
  const showLeftEllipsis = left > 2;
  const showRightEllipsis = right < totalPages - 1;

  const range: (number | "...")[] = [1];
  if (showLeftEllipsis) range.push("...");
  for (let p = left; p <= right; p++) range.push(p);
  if (showRightEllipsis) range.push("...");
  range.push(totalPages);
  return range;
}

export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  siblingCount = 1,
}: PaginationProps) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;

  const range = getPageRange(page, totalPages, siblingCount);

  const btnStyle = (active: boolean): React.CSSProperties => ({
    minWidth: 32,
    padding: "var(--space-xs) var(--space-sm)",
    border: "var(--border-default)",
    background: active ? "var(--color-info)" : "var(--color-surface)",
    color: active ? "var(--color-surface)" : "var(--color-text-primary)",
    cursor: "pointer",
    borderRadius: "var(--radius-sm)",
    fontWeight: active ? 600 : 400,
  });

  return (
    <nav
      aria-label="Pagination"
      style={{
        display: "flex",
        gap: "var(--space-xs)",
        alignItems: "center",
        marginTop: "var(--space-lg)",
      }}
    >
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        aria-label="Prev"
        style={{
          ...btnStyle(false),
          opacity: page <= 1 ? 0.4 : 1,
          cursor: page <= 1 ? "not-allowed" : "pointer",
        }}
      >
        ‹ Prev
      </button>
      {range.map((p, i) =>
        p === "..." ? (
          <span
            key={`e${i}`}
            style={{
              padding: "0 var(--space-xs)",
              color: "var(--color-text-secondary)",
            }}
          >
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            aria-current={p === page ? "page" : undefined}
            style={btnStyle(p === page)}
          >
            {p}
          </button>
        ),
      )}
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        aria-label="Next"
        style={{
          ...btnStyle(false),
          opacity: page >= totalPages ? 0.4 : 1,
          cursor: page >= totalPages ? "not-allowed" : "pointer",
        }}
      >
        Next ›
      </button>
    </nav>
  );
}
